// LayoutOrder.js

const LAYOUT_ORDER_STEP = 100;

export function getLayoutIdentity(layout) {
  return String(layout?.key || layout?.id || '').trim() || null;
}

export function normalizeLayoutPosition(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function getLayoutLabel(layout) {
  return String(layout?.title || '').trim();
}

export function compareLayoutsByConfiguredOrder(left, right) {
  const positionLeft = normalizeLayoutPosition(left?.position);
  const positionRight = normalizeLayoutPosition(right?.position);

  if (positionLeft != null && positionRight != null && positionLeft !== positionRight) {
    return positionLeft - positionRight;
  }

  if (positionLeft != null && positionRight == null) return -1;
  if (positionLeft == null && positionRight != null) return 1;

  const labelCompare = getLayoutLabel(left).localeCompare(getLayoutLabel(right), undefined, {
    sensitivity: 'base',
  });
  if (labelCompare !== 0) return labelCompare;

  return String(getLayoutIdentity(left) || '').localeCompare(String(getLayoutIdentity(right) || ''), undefined, {
    sensitivity: 'base',
  });
}

export function sortLayoutsByConfiguredOrder(layouts) {
  return [...(Array.isArray(layouts) ? layouts : [])].sort(compareLayoutsByConfiguredOrder);
}

export function moveOrderedLayoutById(layouts, fromId, toId) {
  const currentLayouts = Array.isArray(layouts) ? layouts : [];
  const normalizedFromId = String(fromId || '').trim();
  const normalizedToId = String(toId || '').trim();

  if (!normalizedFromId || !normalizedToId || normalizedFromId === normalizedToId) return null;

  const fromIndex = currentLayouts.findIndex((layout) => getLayoutIdentity(layout) === normalizedFromId);
  const toIndex = currentLayouts.findIndex((layout) => getLayoutIdentity(layout) === normalizedToId);

  if (fromIndex < 0 || toIndex < 0) return null;

  const nextLayouts = [...currentLayouts];
  const [movedLayout] = nextLayouts.splice(fromIndex, 1);
  nextLayouts.splice(toIndex, 0, movedLayout);
  return nextLayouts;
}

function buildComparableOrderedLayouts(layouts) {
  const orderedLayouts = sortLayoutsByConfiguredOrder(layouts);
  const explicitPositions = orderedLayouts
    .map((layout) => normalizeLayoutPosition(layout?.position))
    .filter((position) => position != null);

  let nextFallbackPosition = (explicitPositions.length
    ? Math.max(...explicitPositions)
    : 0) + LAYOUT_ORDER_STEP;
  let lastComparablePosition = Number.NEGATIVE_INFINITY;

  return orderedLayouts.map((layout) => {
    const explicitPosition = normalizeLayoutPosition(layout?.position);
    let comparablePosition = explicitPosition != null ? explicitPosition : nextFallbackPosition;

    if (comparablePosition <= lastComparablePosition) {
      comparablePosition = lastComparablePosition + LAYOUT_ORDER_STEP;
    }

    if (explicitPosition == null) {
      nextFallbackPosition = comparablePosition + LAYOUT_ORDER_STEP;
    }

    lastComparablePosition = comparablePosition;

    return {
      ...layout,
      comparablePosition,
    };
  });
}

export function getNextLayoutPosition(layouts) {
  const comparableLayouts = buildComparableOrderedLayouts(layouts);
  const lastComparablePosition = comparableLayouts[comparableLayouts.length - 1]?.comparablePosition;

  if (lastComparablePosition == null) return LAYOUT_ORDER_STEP;
  return lastComparablePosition + LAYOUT_ORDER_STEP;
}

export function getMovedLayoutPosition(layouts, fromId, toId) {
  const orderedLayouts = buildComparableOrderedLayouts(layouts);
  const nextLayouts = moveOrderedLayoutById(orderedLayouts, fromId, toId);
  if (!nextLayouts) return null;

  const normalizedFromId = String(fromId || '').trim();
  const movedIndex = nextLayouts.findIndex((layout) => getLayoutIdentity(layout) === normalizedFromId);
  if (movedIndex < 0) return null;

  const previousComparablePosition = movedIndex > 0
    ? nextLayouts[movedIndex - 1]?.comparablePosition ?? null
    : null;
  const nextComparablePosition = movedIndex < nextLayouts.length - 1
    ? nextLayouts[movedIndex + 1]?.comparablePosition ?? null
    : null;

  if (previousComparablePosition == null && nextComparablePosition == null) {
    return LAYOUT_ORDER_STEP;
  }

  if (previousComparablePosition == null) {
    return nextComparablePosition - LAYOUT_ORDER_STEP;
  }

  if (nextComparablePosition == null) {
    return previousComparablePosition + LAYOUT_ORDER_STEP;
  }

  return previousComparablePosition + ((nextComparablePosition - previousComparablePosition) / 2);
}
